import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { parseISO } from "date-fns";
import { z } from "zod";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export const toCamelCase = (str: string) => {
	return str.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
};

export type CamelCasedObject = { [key: string]: unknown } | unknown[];

export const keysToCamelCase = (obj: unknown): unknown => {
	if (Array.isArray(obj)) {
		return obj.map(v => keysToCamelCase(v));
	} else if (
		obj !== null &&
		typeof obj === "object" &&
		obj.constructor === Object
	) {
		return Object.keys(obj as Record<string, unknown>).reduce(
			(result, key) => {
				(result as Record<string, unknown>)[toCamelCase(key)] =
					keysToCamelCase((obj as Record<string, unknown>)[key]);
				return result;
			},
			{} as Record<string, unknown>
		);
	}
	return obj;
};

// Define format types for better type safety
type DateTimeFormatType = "dateTime" | "timeOnly" | "dateOnly";

export const formatDateTime = (
	dateString: string | null,
	formatType: DateTimeFormatType = "dateTime" // Default to full date and time
) => {
	if (!dateString) return "N/A";

	const date = parseISO(dateString);
	const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

	// Configure format options based on type
	const getFormatOptions = (
		type: DateTimeFormatType
	): Intl.DateTimeFormatOptions => {
		const baseOptions = { timeZone: userTimeZone };

		switch (type) {
			case "timeOnly":
				return {
					...baseOptions,
					hour: "numeric",
					minute: "2-digit",
				};
			case "dateOnly":
				return {
					...baseOptions,
					year: "numeric",
					month: "short",
					day: "numeric",
				};
			case "dateTime":
			default:
				return {
					...baseOptions,
					year: "numeric",
					month: "short",
					day: "numeric",
					hour: "numeric",
					minute: "2-digit",
				};
		}
	};

	// First formatter for date and/or time
	const mainFormatter = new Intl.DateTimeFormat(
		"en-US",
		getFormatOptions(formatType)
	);

	// Second formatter for timezone name (only included for dateTime and timeOnly)
	const shouldShowTimeZone = formatType !== "dateOnly";
	let result = mainFormatter.format(date);

	if (shouldShowTimeZone) {
		const timeZoneFormatter = new Intl.DateTimeFormat("en-US", {
			timeZone: userTimeZone,
			timeZoneName: "long",
		});

		const timeZoneString =
			timeZoneFormatter
				.formatToParts(date)
				.find(part => part.type === "timeZoneName")?.value || "";

		result += ` (${timeZoneString})`;
	}

	return result;
};

export async function generateMistralEmbedding(
	text: string
): Promise<number[] | null> {
	try {
		const response = await fetch("https://api.mistral.ai/v1/embeddings", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${process.env.MISTRAL_API_KEY}`,
			},
			body: JSON.stringify({
				input: text,
				model: "mistral-embed",
			}),
		});

		if (!response.ok) {
			throw new Error(
				`Mistral API request failed with status ${response.status}`
			);
		}

		const result = await response.json();
		return result.data[0].embedding;
	} catch (error) {
		console.error("Error generating Mistral embedding:", error);
		return null;
	}
}

export const isValidUuid = (str: string): boolean => {
	const uuid = z.string().uuid().safeParse(str);
	return uuid.success;
};
